import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { Cohort, cohortHasRoom } from 'TenantModule/api/getCohortsApi';

export type CohortSelectorProps = {
  cohorts: Cohort[];
  isLoading: boolean;
  isError: boolean;
  value: number | '';
  onChange: (cohortId: number) => void;
  disabled?: boolean;
};

/**
 * Enterprise group-filling picker (RDM-2031/GLX-1865): every ACTIVE Enterprise
 * cohort with its X/Y fill; full cohorts render disabled. The server orders the
 * list least-filled first, and the form defaults to the first entry with room —
 * annotated so the pre-selection doesn't surprise operators.
 */
export const CohortSelector = ({
  cohorts,
  isLoading,
  isError,
  value,
  onChange,
  disabled,
}: CohortSelectorProps) => {
  const firstWithRoom = cohorts.find(cohortHasRoom);
  const isEmpty = !isLoading && cohorts.length === 0;
  const allFull = cohorts.length > 0 && !firstWithRoom;

  return (
    <FormControl fullWidth error={isError || allFull}>
      <InputLabel id="cohort-selector-label">Enterprise cohort</InputLabel>
      <Select<number | ''>
        labelId="cohort-selector-label"
        label="Enterprise cohort"
        data-testid="cohort-selector"
        value={value}
        onChange={(e) => {
          // MUI can emit '' (initial/cleared); Number('') is 0, which downstream
          // would look like a real cohort id and unblock submit.
          const raw = e.target.value;
          if (raw === '' || raw == null) {
            return;
          }
          const next = Number(raw);
          if (Number.isNaN(next) || next <= 0) {
            return;
          }
          onChange(next);
        }}
        disabled={disabled || isLoading || isEmpty || allFull}
      >
        {cohorts.map((cohort) => (
          <MenuItem key={cohort.id} value={cohort.id} disabled={!cohortHasRoom(cohort)}>
            {`${cohort.cohortId} (${cohort.currentCount}/${cohort.capacityLimit})`}
            {cohort.id === firstWithRoom?.id ? ' — least filled' : ''}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {isLoading
          ? 'Loading available Enterprise cohorts…'
          : isError
            ? 'Failed to load cohorts — retry or contact infra'
            : isEmpty
              ? 'No Enterprise cohorts available — register one (or contact infra)'
              : allFull
                ? 'All cohorts full. Spin up a new Enterprise cluster via mosaic-infra.'
                : "The tenant's databases are created on the cohort's cluster(s)"}
      </FormHelperText>
    </FormControl>
  );
};
